import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { API_CONFIG } from '../config';
import { ICONS } from '../constants';
import Button from './ui/Button';
import ConfirmationModal from './ui/ConfirmationModal';

interface ActivityLog {
  id: string;
  user_id: string | null;
  username: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  entity_name: string | null;
  details: Record<string, any> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

interface LogsResponse {
  logs: ActivityLog[];
  total: number;
  limit: number;
  offset: number;
}

interface Stats {
  total: number;
  byAction: Array<{ action: string; count: string }>;
  topUsers: Array<{ username: string; count: string }>;
  last24Hours: number;
}

const ActivityLogsTab: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(20);
  
  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Delete confirmation modal
  const [deleteLogsModal, setDeleteLogsModal] = useState<{ isOpen: boolean; type: 'old' | 'all' }>({ isOpen: false, type: 'old' });

  const token = localStorage.getItem('spellbook_token');

  // Fetch logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      if (actionFilter) params.append('action', actionFilter);
      if (entityTypeFilter) params.append('entityType', entityTypeFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await fetch(`${API_CONFIG.API_URL}/activity-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data: LogsResponse = await response.json();
        setLogs(data.logs);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_CONFIG.API_URL}/activity-logs/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data: Stats = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [page, actionFilter, entityTypeFilter, startDate, endDate]);

  // Export logs
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.append('action', actionFilter);
      if (entityTypeFilter) params.append('entityType', entityTypeFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await fetch(`${API_CONFIG.API_URL}/activity-logs/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `spellbook-logs-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to export logs:', error);
    }
  };

  // Delete logs - open confirmation modal
  const handleDelete = (type: 'old' | 'all') => {
    setDeleteLogsModal({ isOpen: true, type });
  };
  
  // Confirm delete logs
  const confirmDeleteLogs = async () => {
    const { type } = deleteLogsModal;
    setDeleteLogsModal({ isOpen: false, type: 'old' });

    try {
      const body = type === 'all' 
        ? { deleteAll: true }
        : { olderThan: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString() };

      const response = await fetch(`${API_CONFIG.API_URL}/activity-logs`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        fetchLogs();
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to delete logs:', error);
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get action badge color
  const getActionColor = (action: string) => {
    switch (action) {
      case 'LOGIN': return 'bg-green-500';
      case 'LOGOUT': return 'bg-gray-500';
      case 'CREATE': return 'bg-blue-500';
      case 'UPDATE': return 'bg-yellow-500';
      case 'DELETE': return 'bg-red-500';
      case 'EXPORT': return 'bg-purple-500';
      case 'PASSWORD_RESET': return 'bg-orange-500';
      case 'EMAIL_CHANGE': return 'bg-indigo-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gray-700 p-4 rounded-lg">
            <div className="text-gray-400 text-xs mb-1">{t('activityLogs.totalLogs')}</div>
            <div className="text-2xl font-bold">{stats.total}</div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg">
            <div className="text-gray-400 text-xs mb-1">{t('activityLogs.last24Hours')}</div>
            <div className="text-2xl font-bold">{stats.last24Hours}</div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg">
            <div className="text-gray-400 text-xs mb-1">{t('activityLogs.topAction')}</div>
            <div className="text-lg font-bold">
              {stats.byAction[0]?.action || 'N/A'} ({stats.byAction[0]?.count || 0})
            </div>
          </div>
          <div className="bg-gray-700 p-4 rounded-lg">
            <div className="text-gray-400 text-xs mb-1">{t('activityLogs.topUser')}</div>
            <div className="text-lg font-bold truncate">
              {stats.topUsers[0]?.username || 'N/A'} ({stats.topUsers[0]?.count || 0})
            </div>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="bg-gray-700 p-4 rounded-lg space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
            className="bg-gray-600 text-white rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">{t('activityLogs.allActions')}</option>
            <option value="LOGIN">LOGIN</option>
            <option value="LOGOUT">LOGOUT</option>
            <option value="CREATE">CREATE</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
            <option value="EXPORT">EXPORT</option>
            <option value="PASSWORD_RESET">PASSWORD_RESET</option>
            <option value="EMAIL_CHANGE">EMAIL_CHANGE</option>
          </select>

          <select
            value={entityTypeFilter}
            onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(0); }}
            className="bg-gray-600 text-white rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">{t('activityLogs.allEntities')}</option>
            <option value="USER">USER</option>
            <option value="ARTIST">ARTIST</option>
            <option value="PROJECT">PROJECT</option>
            <option value="INVITE">INVITE</option>
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
            className="bg-gray-600 text-white rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            placeholder={t('activityLogs.startDate')}
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
            className="bg-gray-600 text-white rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
            placeholder={t('activityLogs.endDate')}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleExport}
            variant="primary"
            size="sm"
            className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700"
          >
            <ICONS.Download className="w-4 h-4" />
            <span>{t('activityLogs.exportTxt')}</span>
          </Button>

          <Button
            onClick={() => handleDelete('old')}
            variant="secondary"
            size="sm"
            className="flex items-center space-x-2 bg-yellow-600 hover:bg-yellow-700"
          >
            <ICONS.Trash className="w-4 h-4" />
            <span>{t('activityLogs.deleteOld')}</span>
          </Button>

          <Button
            onClick={() => handleDelete('all')}
            variant="secondary"
            size="sm"
            className="flex items-center space-x-2 bg-red-600 hover:bg-red-700"
          >
            <ICONS.Trash className="w-4 h-4" />
            <span>{t('activityLogs.deleteAll')}</span>
          </Button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-gray-700 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">{t('common.loading')}</div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-400">{t('activityLogs.noLogs')}</div>
        ) : (
          <div className="overflow-x-auto max-h-120 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs">{t('activityLogs.timestamp')}</th>
                  <th className="px-3 py-2 text-left text-xs">{t('activityLogs.user')}</th>
                  <th className="px-3 py-2 text-left text-xs">{t('activityLogs.action')}</th>
                  <th className="px-3 py-2 text-left text-xs">{t('activityLogs.entity')}</th>
                  <th className="px-3 py-2 text-left text-xs">{t('activityLogs.ip')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => (
                  <tr key={log.id} className={index % 2 === 0 ? 'bg-gray-700' : 'bg-gray-750'}>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {log.username}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 text-xs rounded ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {log.entity_type && (
                        <div>
                          <div className="font-medium">{log.entity_type}</div>
                          {log.entity_name && (
                            <div className="text-gray-400 text-xs truncate max-w-xs">{log.entity_name}</div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-400">
                      {log.ip_address}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between p-3 bg-gray-600 border-t border-gray-500">
            <div className="text-xs text-gray-400">
              {t('activityLogs.showing', { 
                start: page * limit + 1, 
                end: Math.min((page + 1) * limit, total), 
                total 
              })} 
              <span className="text-gray-500 ml-2">
                (стр. {page + 1} из {Math.ceil(total / limit)})
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-3 py-1 rounded bg-gray-500 hover:bg-gray-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center"
                title="Предыдущая страница"
              >
                <ICONS.ChevronLeft className="w-4 h-4" />
              </button>
              <span className="flex items-center px-2 text-sm text-gray-300">
                {page + 1}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * limit >= total}
                className="px-3 py-1 rounded bg-gray-500 hover:bg-gray-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center"
                title="Следующая страница"
              >
                <ICONS.ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Delete Logs Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteLogsModal.isOpen}
        onClose={() => setDeleteLogsModal({ isOpen: false, type: 'old' })}
        onConfirm={confirmDeleteLogs}
        title={t('activityLogs.deleteTitle') || 'Удалить логи'}
        message={deleteLogsModal.type === 'all' 
          ? t('activityLogs.confirmDeleteAll') || 'Удалить все логи активности? Это действие нельзя отменить.'
          : t('activityLogs.confirmDeleteOld') || 'Удалить логи старше 30 дней? Это действие нельзя отменить.'}
        confirmText={t('common.delete') || 'Удалить'}
        cancelText={t('common.cancel') || 'Отмена'}
        type="danger"
      />
    </div>
  );
};

export default ActivityLogsTab;

